// 流量监控的接口与换算（docs/04 第 8 节，决策 D98）：提交与送达都按消息的接收时刻归入时段，送达率是这批消息的送达率。
import { fromBeijing, toBeijing, type ChartTipRow } from "@/ui";
import { api } from "./api";

/** 一段时间里的提交与送达数。 */
export interface TrafficCount { submitted: number; delivered: number }

/** 列表的一行：一个客户在一个 MCC 上今天到现在、昨天同一时段与昨天全天的数（北京日，D174）。customer_id 为 0 是已删除的账号。 */
export interface TrafficRow {
  customer_id: number; mcc: string; country?: string; account_ids: number[];
  today: TrafficCount; same_period: TrafficCount; yesterday: TrafficCount;
}

export interface TrafficList { now: string; rows: TrafficRow[] }

/** 一条序列，与 TrafficDetail.times 逐项对齐；id 是通道或账号，总量为 0。 */
export interface TrafficSeries { id: number; submitted: number[]; delivered: number[] }

/** 详情：total 与 accounts 按消息计，channels 按尝试计（一条消息重试到第二个通道，两个通道各算一次）。 */
export interface TrafficDetail {
  customer_id: number; mcc: string; country?: string;
  times: string[]; total: TrafficSeries; accounts: TrafficSeries[]; channels: TrafficSeries[];
}

/** 详情看的北京日数，含今天。 */
export type TrafficDays = 1 | 2 | 7;

/** 详情序列的时段：小时或 10 分钟（D194，只有 1、2 天可选）。分日由前端把小时相加。 */
export type TrafficStep = "hour" | "10m";

/** 查询串：值为空的参数不带。 */
function query(params: Record<string, string>): string {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "")).toString();
  return q ? `?${q}` : "";
}

export const traffic = {
  /** accountId 为空是全部账号；fresh 是手动刷新，跳过 admin 端的 10 秒缓存。 */
  list: (accountId = "", fresh = false) => api<TrafficList>(`/traffic${query({ account_id: accountId, fresh: fresh ? "1" : "" })}`),
  /** accountId 为空是这个客户的全部账号。 */
  detail: (customerId: number, mcc: string, days: TrafficDays, step: TrafficStep, accountId = "") =>
    api<TrafficDetail>(`/traffic/detail${query({ customer_id: String(customerId), mcc, days: String(days), step, account_id: accountId })}`),
};

/** 送达率，没有提交时为 null。 */
export function rateOf(c: TrafficCount): number | null {
  return c.submitted > 0 ? c.delivered / c.submitted : null;
}

/** 相对变化 (now − before) / before，before 为 0 时没有可比的基数，为 null。 */
export function changeOf(now: number, before: number): number | null {
  return before > 0 ? (now - before) / before : null;
}

/** 送达率之差，单位是百分点；任一边没有提交时为 null。 */
export function rateDiff(now: TrafficCount, before: TrafficCount): number | null {
  const a = rateOf(now), b = rateOf(before);
  return a === null || b === null ? null : (a - b) * 100;
}

/** 数量的千分位。 */
export function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

/** 比率的百分数，一位小数；null 显示"—"。 */
export function formatRate(r: number | null): string {
  return r === null ? "—" : `${(r * 100).toFixed(1)}%`;
}

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["zh-CN"], { type: "region" });
  } catch {
    return null;
  }
})();

/** ISO 两位国家代码的中文名，认不出时原样返回。 */
export function countryName(code?: string): string {
  if (!code) return "";
  try {
    return regionNames?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** 一段时间点与对齐的几组数：分时就是小时序列本身，分日把同一北京日的小时相加。 */
export interface Buckets { times: string[]; values: number[][] }

/** 把小时序列按北京日（D174）相加；hours（小时时段的起点）与 values 的每一组逐项对齐，返回的 times 是各北京日零点的时刻。 */
export function sumByDay(hours: string[], values: number[][]): Buckets {
  const times: string[] = [];
  const out = values.map(() => [] as number[]);
  hours.forEach((h, i) => {
    const b = toBeijing(new Date(h));
    const day = fromBeijing(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate()).toISOString();
    if (times[times.length - 1] !== day) {
      times.push(day);
      out.forEach((o) => o.push(0));
    }
    out.forEach((o, k) => (o[o.length - 1] += values[k][i]));
  });
  return { times, values: out };
}

/** 横轴与提示框的北京时刻：分时 MM/DD HH:mm，分日 MM/DD；full 为真时带年份（提示框）。 */
export function formatTick(iso: string, daily: boolean, full = false): string {
  const d = toBeijing(new Date(iso));
  const p = (x: number) => String(x).padStart(2, "0");
  const date = `${p(d.getUTCMonth() + 1)}/${p(d.getUTCDate())}`;
  const head = full ? `${d.getUTCFullYear()}/${date}` : date;
  return daily ? head : `${head} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/** 图上的一条序列：values 与横轴的时刻逐项对齐；比率图的值是 0 到 1，没有样本时为 null（断线）。 */
export interface ChartSeries { name: string; color: string; values: (number | null)[] }

/** 提示框的行：第 i 个时刻各序列的值，按数值从大到小排，没有值的排最后。 */
export function tipRows(series: ChartSeries[], i: number, rate: boolean): ChartTipRow[] {
  return series
    .map((s) => ({ s, v: s.values[i] ?? null }))
    .sort((a, b) => (b.v ?? -1) - (a.v ?? -1))
    .map(({ s, v }) => ({ name: s.name, color: s.color, value: rate ? formatRate(v) : v === null ? "—" : formatCount(v) }));
}
